import type { VariableAllocationRow, VariableAllocationTable } from 'types/comp-allocations'
import type { CompensationPlan, ExpandedCompensationPlan } from 'types/comp-plan'

import dayjs from 'dayjs'
import React from 'react'
import type { ExpandedCompensationTemplate } from 'types/comp-template'
import type { TargetDTO } from 'types/components'

interface CreateplanDataWrapperProps {
  compensationTemplate?: ExpandedCompensationTemplate
  existingPlan?: ExpandedCompensationPlan
  showOriginalEffectiveDate?: boolean
  withUserId?: string
  withBaseSalary?: number
  withVariablePercent?: number
  withVariableCap?: number
  withAllocations?: number[]
  withAccelerators?: number[]
  withStartDate?: string
  review?: boolean
  employeeName?: string
  children: React.ReactNode
}

interface CreatePlanContextValue {
  compensationTemplate: ExpandedCompensationTemplate | null | undefined
  templateComponents: VariableAllocationTable[]
  setTemplateComponents: any
  templateComponentsInPlan: VariableAllocationTable[]
  setTemplateComponentsInPlan: any
  plan: CompensationPlan
  setPlan: any
  validPlanConfig: boolean
  setValidPlanConfig: any
  mode: 'CREATE' | 'EDIT' | 'REVIEW'
  setMode: any
  totalComponentsInPlanValue: number
  maxVariableAllowedInComponents: number
  employeeName: string
  setEmployeeName: any
  setMaxVariableAllowedInComponents: any
  targetDTOs: TargetDTO[]
  setTargetDTOs: any
  originalEffectiveDate: string | null
  showOriginalEffectiveDate: boolean
  withUserId?: string
  withBaseSalary?: number
  withVariablePercent?: number
  withVariableCap?: number
  withAllocations?: number[]
  withAccelerators?: number[]
  withStartDate?: string
  review?: boolean
}

const CreatePlanContext = React.createContext<CreatePlanContextValue>({
  compensationTemplate: null,
  templateComponents: [],
  setTemplateComponents: () => {},
  templateComponentsInPlan: [],
  setTemplateComponentsInPlan: () => {},
  plan: {
    id: null,
    revision: 0,
    userId: '',
    templateId: null,
    templateRevision: null,
    organizationId: '',
    rootOrganizationId: '',
    rootPeriodId: '',
    periodId: '',
    periodProration: 1.0,
    description: '',
    compensationModel: 'BASE_PLUS',
    baseSalary: 0,
    variablePercent: 0.0,
    variableCap: 0,
    variableAllocations: [],
    effectiveDate: '',
    createdAt: '',
    updatedAt: '',
  },
  setPlan: () => {},
  validPlanConfig: false,
  setValidPlanConfig: () => {},
  mode: 'CREATE',
  setMode: () => {},
  totalComponentsInPlanValue: 0,
  maxVariableAllowedInComponents: 0,
  employeeName: '',
  setEmployeeName: () => {},
  setMaxVariableAllowedInComponents: () => {},
  targetDTOs: [],
  setTargetDTOs: () => {},
  originalEffectiveDate: null,
  showOriginalEffectiveDate: false,
  withUserId: undefined,
  withBaseSalary: undefined,
  withVariablePercent: undefined,
  withVariableCap: undefined,
  withAllocations: undefined,
  withAccelerators: undefined,
  withStartDate: undefined,
  review: undefined,
})

const CreatePlanContextProvider: React.FC<CreateplanDataWrapperProps> = ({
  compensationTemplate,
  existingPlan,
  showOriginalEffectiveDate,
  withUserId,
  withBaseSalary,
  withVariablePercent,
  withVariableCap,
  withAllocations,
  withAccelerators,
  withStartDate,
  review,
  employeeName: initialEmployeeName,
  children,
}) => {
  const tempPlan: CompensationPlan = React.useMemo(() => {
    if (existingPlan) {
      return {
        ...existingPlan.plan,
        effectiveDate: existingPlan.plan.effectiveDate,
        showOriginalEffectiveDate,
      }
    } else if (compensationTemplate) {
      let variableAllocations = compensationTemplate.template.variableAllocations
      if (withAllocations && withAccelerators) {
        let allocationIndex = 0
        variableAllocations = variableAllocations.map((table) => ({
          ...table,
          rows: table.rows.map((row) => {
            if (row.rowType !== 'DISPLAY_ROW' && !row.disabled && allocationIndex < withAllocations.length) {
              const allocation = withAllocations[allocationIndex]
              const accelerator = withAccelerators[allocationIndex]
              allocationIndex++
              return {
                ...row,
                allocationPercentage: allocation,
                maxAllocationPercentage: accelerator,
              }
            }
            return row
          }),
        }))
      }

      console.log('withStartDate', withStartDate)

      return {
        id: null,
        revision: 0,
        userId: withUserId || '',
        templateId: compensationTemplate.template.id || null,
        templateRevision: compensationTemplate.template.revision || null,
        organizationId: compensationTemplate.template.rootOrganizationId,
        rootOrganizationId: compensationTemplate.template.rootOrganizationId,
        rootPeriodId: compensationTemplate.template.rootPeriodId,
        periodId: compensationTemplate.template.periodId,
        periodProration: compensationTemplate.template.periodProration,
        description: compensationTemplate.template.position,
        compensationModel: compensationTemplate.template.compensationModel,
        baseSalary: withBaseSalary || 0.0,
        variablePercent: withVariablePercent || compensationTemplate.template.variablePercent,
        variableCap: withVariableCap !== undefined ? withVariableCap : compensationTemplate.template.variableCap,
        variableAllocations,
        effectiveDate: withStartDate
          ? dayjs(withStartDate).utc().format('YYYY-MM-DDTHH:mm:ss.SSSZ')
          : dayjs().utc().format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
        showOriginalEffectiveDate,
        createdAt: '',
        updatedAt: '',
      } as CompensationPlan
    } else {
      return {
        id: null,
        revision: 0,
        userId: withUserId || '',
        templateId: null,
        templateRevision: null,
        organizationId: '',
        rootOrganizationId: '',
        rootPeriodId: '',
        periodId: '',
        periodProration: 1.0,
        description: '',
        compensationModel: 'BASE_PLUS',
        baseSalary: withBaseSalary || 0.0,
        variablePercent: withVariablePercent || 0,
        variableCap: withVariableCap || 0,
        variableAllocations: [],
        effectiveDate: withStartDate
          ? dayjs(withStartDate).utc().format('YYYY-MM-DDTHH:mm:ss.SSSZ')
          : dayjs().utc().format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
        showOriginalEffectiveDate,
        createdAt: '',
        updatedAt: '',
      }
    }
  }, [
    existingPlan,
    compensationTemplate,
    showOriginalEffectiveDate,
    withUserId,
    withBaseSalary,
    withVariablePercent,
    withVariableCap,
    withAllocations,
    withAccelerators,
    withStartDate,
  ])

  const [plan, setPlan] = React.useState<CompensationPlan>(tempPlan)

  const [templateComponentsInPlan, setTemplateComponentsInPlan] = React.useState<VariableAllocationTable[]>(() => {
    if (existingPlan?.plan?.variableAllocations) {
      return existingPlan.plan.variableAllocations.map((category) => ({ label: category.label, rows: category.rows }))
    } else if (compensationTemplate?.template?.variableAllocations) {
      return compensationTemplate.template.variableAllocations.map((category) => ({ label: category.label, rows: [] }))
    }
    return []
  })

  const [templateComponents, setTemplateComponents] = React.useState<VariableAllocationTable[]>(() => {
    if (existingPlan?.plan?.variableAllocations) {
      return JSON.parse(JSON.stringify(existingPlan.plan.variableAllocations))
    } else if (compensationTemplate?.template?.variableAllocations) {
      return JSON.parse(JSON.stringify(compensationTemplate.template.variableAllocations))
    }
    return []
  })

  const [totalComponentsInPlanValue, setTotalComponentsInPlanValue] = React.useState<number>(0)
  const [maxVariableAllowedInComponents, setMaxVariableAllowedInComponents] = React.useState<number>(0)
  const [mode, setMode] = React.useState<'CREATE' | 'EDIT' | 'REVIEW'>(review ? 'REVIEW' : 'CREATE')
  const [validPlanConfig, setValidPlanConfig] = React.useState<boolean>(false)
  const [employeeName, setEmployeeName] = React.useState<string>(initialEmployeeName || '')

  const [targetDTOs, setTargetDTOs] = React.useState<TargetDTO[]>(() => {
    if (existingPlan?.targets) {
      return existingPlan.targets.map((target) => ({
        componentId: target.componentId,
        label: target.label,
        value: target.value,
        rootOrganizationId: target.rootOrganizationId,
        organizationId: target.organizationId,
        rootPeriodId: target.rootPeriodId,
        periodId: target.periodId,
        slug: target.slug,
        userId: target.userId,
      }))
    } else if (compensationTemplate?.targets) {
      return compensationTemplate.targets.map((target) => ({
        componentId: target.componentId,
        label: target.label,
        value: target.value,
        rootOrganizationId: target.rootOrganizationId,
        organizationId: target.organizationId,
        rootPeriodId: target.rootPeriodId,
        periodId: target.periodId,
        slug: target.slug,
        userId: null,
      }))
    }
    return []
  })

  const [originalEffectiveDate, setOriginalEffectiveDate] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (existingPlan?.plan?.effectiveDate) {
      setOriginalEffectiveDate(existingPlan.plan.effectiveDate)
    }
  }, [existingPlan])

  React.useEffect(() => {
    const validatePlanConfig = (allocationTotal: number): boolean => {
      if (
        allocationTotal === 100 &&
        plan.userId !== '' &&
        plan.baseSalary >= 0 &&
        plan.variablePercent > 0 &&
        plan.variableCap >= 0 &&
        plan.variablePercent <= plan.variableCap
      ) {
        return true
      } else {
        return false
      }
    }

    let allocationTotal = 0
    let maxVariableAllowed = 0

    if (plan.variableAllocations) {
      plan.variableAllocations.forEach((table) => {
        table.rows.forEach((row: VariableAllocationRow) => {
          const disabled = row.disabled != undefined && row.disabled != null && row.disabled
          if (row.rowType !== 'DISPLAY_ROW' && !disabled) {
            allocationTotal += row.allocationPercentage * 100
            const maxPayout =
              row.maxAllocationPercentage != null && row.maxAllocationPercentage != 0
                ? plan.baseSalary * row.maxAllocationPercentage * plan.variablePercent
                : plan.baseSalary * row.allocationPercentage * plan.variablePercent

            maxVariableAllowed += maxPayout
          }
        })
      })
    }

    setTotalComponentsInPlanValue(allocationTotal)
    setMaxVariableAllowedInComponents(maxVariableAllowed)
    setValidPlanConfig(validatePlanConfig(allocationTotal))
  }, [templateComponentsInPlan, plan])

  const value: CreatePlanContextValue = {
    compensationTemplate: compensationTemplate || null,
    templateComponents,
    setTemplateComponents,
    templateComponentsInPlan,
    setTemplateComponentsInPlan,
    plan,
    setPlan,
    validPlanConfig,
    setValidPlanConfig,
    mode,
    setMode,
    totalComponentsInPlanValue,
    maxVariableAllowedInComponents,
    employeeName,
    setEmployeeName,
    setMaxVariableAllowedInComponents,
    targetDTOs,
    setTargetDTOs,
    originalEffectiveDate,
    showOriginalEffectiveDate: showOriginalEffectiveDate || false,
    withUserId,
    withBaseSalary,
    withVariablePercent,
    withVariableCap,
    withAllocations,
    withAccelerators,
    withStartDate,
    review,
  }

  return <CreatePlanContext.Provider value={value}>{children}</CreatePlanContext.Provider>
}

function useCreatePlanContext() {
  const context = React.useContext(CreatePlanContext)
  if (context === undefined) {
    throw new Error('useCreatePlanContext must be used within a CreatePlanContextProvider')
  }
  return context
}

export { CreatePlanContextProvider, useCreatePlanContext }
